import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as api from '../services/dataCombApi'
import { sameFindingsPass, shouldRetryFindingLink } from '../components/data-comb/combFormat'
import type { FindingsQuery } from '../types/dataComb'
import { SLOW } from '../utils/polling'

/**
 * Data Comb queries.
 *
 * Only the summary polls, and slowly. The comb runs once a night, so the findings
 * lists would re-fetch an identical page every time; mutations invalidate what
 * they changed instead.
 */

const KEY = 'data-comb'

/**
 * The latest pass's summary, on SLOW.
 *
 * It is the one comb query that sits beside live cards: the Overview's Critical
 * Attention reads it next to halts and stale runs, which poll. Unpolled, that card
 * was a snapshot from page load that never noticed the 04:15 pass land, and a
 * stale all-clear beside two live ones reads as current. Two minutes is enough to
 * notice a nightly pass without re-reading an unchanged payload every few seconds.
 */
export function useCombSummary() {
  return useQuery({ queryKey: [KEY, 'summary'], queryFn: api.getCombSummary, refetchInterval: SLOW })
}

/**
 * Recent passes, newest first — Running ones included, since the server's list
 * does not filter on completion. `enabled` lets the page ask for a longer list
 * only when a deep-linked pass is not among the short one.
 */
export function useCombRuns(limit = 20, enabled = true) {
  return useQuery({ queryKey: [KEY, 'runs', limit], queryFn: () => api.getCombRuns(limit), enabled })
}

export function useCombFindings(query: FindingsQuery, enabled = true) {
  return useQuery({
    queryKey: [KEY, 'findings', query],
    queryFn: () => api.getCombFindings(query),
    enabled,
    // Keeps the previous page on screen while the next one loads, so paging and
    // filtering do not blank the table on every keystroke — but only within one
    // pass. Switching passes shows the loading state instead: the previous pass's
    // rows under the next pass's header would be findings attributed to a pass
    // that did not record them.
    placeholderData: (previous, previousQuery) =>
      sameFindingsPass(previousQuery?.queryKey, query.runId) ? previous : undefined,
  })
}

/**
 * One finding by id, for a `?finding=` deep link.
 *
 * A 404 is an answer, not a blip, so it is not retried: the id is past the
 * retention window or never existed, and asking again changes neither.
 */
export function useCombFinding(id: string | null) {
  return useQuery({
    queryKey: [KEY, 'finding', id],
    queryFn: () => api.getCombFinding(id!),
    enabled: Boolean(id),
    retry: shouldRetryFindingLink,
  })
}

/**
 * A column's fill-rate history, loaded only when a detail panel asks for it.
 *
 * Disabled for table-level findings — landing residue and a skipped table have no
 * column, and requesting a history for one would 200 with an empty series that
 * reads like "no history exists" rather than "this question does not apply".
 */
export function useColumnHistory(
  schemaName: string | null,
  tableName: string | null,
  columnName: string | null,
  limit = 30,
) {
  const enabled = Boolean(schemaName && tableName && columnName)
  return useQuery({
    queryKey: [KEY, 'columnHistory', schemaName, tableName, columnName, limit],
    queryFn: () => api.getColumnHistory(schemaName!, tableName!, columnName!, limit),
    enabled,
  })
}

/**
 * Acknowledging and restoring.
 *
 * Both invalidate the summary as well as the list: the severity chips are counts
 * over the whole run, so a row leaving the outstanding list has to change the
 * header too or the two disagree on screen.
 */
export function useAcknowledgeFinding() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, note }: { id: string; note?: string }) => api.acknowledgeFinding(id, note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] })
    },
  })
}

export function useUnacknowledgeFinding() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.unacknowledgeFinding(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [KEY] })
    },
  })
}
